import { format, m, type Message } from '@/i18n';
/**
 * **A server action that cannot reach the server** (docs/LOADING.md §1, §4.5):
 * the seam that turns a NETWORK FAILURE into a value the surface can speak,
 * where the act was taken, instead of a rejected promise that React rethrows
 * from the transition to the nearest error boundary.
 *
 * A reply is a confirmation or a refusal (a typed `DomainError`, returned as a
 * `Result`); a network failure is no reply at all - the request did not arrive,
 * or its reply did not come back - and a server action then REJECTS. Only that
 * kind of rejection becomes a value here: anything else (a render bug, a
 * server-side throw) still throws, because that is what the error boundaries
 * are for.
 *
 * An act on the offline queue does not come through here: `runOrQueue`
 * (`src/components/live-queue.ts`) DEFERS a network failure instead of
 * reporting it. This is for the acts the queue cannot carry.
 */

/** What a surface says when an act could not reach the server (the dialogs'
 *  wording, docs/LOADING.md §4.5). */
export const UNREACHABLE_MESSAGE = format('en', m('common.unreachable'));

/** The value a network failure becomes. Shaped like a refusal (`ok: false` with
 *  an `error.message`), so a surface that already speaks refusals speaks this
 *  too; `unreached` tells the two apart where a surface must (a refusal is
 *  final, a network failure may be retried). Not a `DomainError`: the domain
 *  said nothing. */
export interface Unreached {
  ok: false;
  unreached: true;
  /** Set when no reply came within `REPLY_LIMIT_MS` and the request may still
   *  answer (docs/LOADING.md §5.6); absent for a request that failed. */
  late?: true;
  error: { code: 'unreachable'; message: string; descriptor: Message };
}

export const UNREACHED: Unreached = {
  ok: false,
  unreached: true,
  error: { code: 'unreachable', message: UNREACHABLE_MESSAGE, descriptor: m('common.unreachable') },
};

/** Whether the device believes it is offline right now. */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Whether a failure is the network refusing, rather than the server answering
 *  or the code failing. A server action that cannot reach the server rejects
 *  with the `TypeError` that `fetch` throws in every engine ("Failed to
 *  fetch", "NetworkError when attempting to fetch resource.", "Load failed");
 *  a domain refusal comes back as a `Result`, and anything else is a bug that
 *  belongs to the error boundary. Neither the message nor `navigator.onLine`
 *  decides it: a bug thrown while offline is still a bug. */
export function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError;
}

/**
 * How long an act's reply may take before the surface stops waiting
 * (docs/LOADING.md §5.6): 10 s, Nielsen's limit for keeping attention on the
 * dialogue. docs/RESOLUTION.md §10 targets a confirmed tap under 300 ms on
 * mobile, so the limit sits far beyond any healthy round trip. An act with no
 * reply by then - no confirmation, no refusal, no network error - is treated
 * as a network failure: `runOrQueue` defers it, `reach` reports it. The tap
 * mount waits the same 10 s for a navigation that never commits
 * (`src/components/tap-mount.tsx`): one number, one name.
 */
export const REPLY_LIMIT_MS = 10_000;

/**
 * The reply of `attempt`, if it comes within `limitMs`; `{ late: true }` if it
 * does not. A rejection inside the limit is rethrown. The attempt is not
 * cancelled (a server action cannot be): the caller keeps the promise and
 * decides what its late reply does.
 */
export async function withinReplyLimit<T>(
  attempt: Promise<T>,
  limitMs: number = REPLY_LIMIT_MS,
): Promise<{ late: false; reply: T } | { late: true }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const limit = new Promise<{ late: true }>((resolve) => {
    timer = setTimeout(() => resolve({ late: true }), limitMs);
  });
  try {
    return await Promise.race([attempt.then((reply) => ({ late: false as const, reply })), limit]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Run a server action; a network failure comes back as `UNREACHED` instead of a
 * throw. Every other rejection is rethrown untouched.
 *
 * **At most `REPLY_LIMIT_MS`** (docs/LOADING.md §5.6): an act with no reply by
 * then comes back as `UNREACHED` too, so the surface withdraws its reading and
 * says the server could not be reached. The request itself keeps going, and
 * `onLate` hears its reply when it arrives: a surface can then clear its notice
 * on a late confirmation, or speak a late refusal. A late network failure is no
 * reply and is not reported (the surface already says so); a late rejection of
 * any other kind has no boundary left to reach, so it is logged.
 */
export async function reach<T>(
  run: () => Promise<T>,
  opts: { onLate?: (reply: T) => void; limitMs?: number } = {},
): Promise<T | Unreached> {
  try {
    const attempt = run();
    const answer = await withinReplyLimit(attempt, opts.limitMs);
    if (!answer.late) return answer.reply;
    attempt.then(
      (reply) => opts.onLate?.(reply),
      (error: unknown) => {
        if (!isNetworkFailure(error)) console.error(error);
      },
    );
    return { ...UNREACHED, late: true };
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    return UNREACHED;
  }
}
